import React, { useState, useEffect, useRef, useCallback } from 'react'
import CommunityCategoryFilter from './CommunityCategoryFilter'
import CommunityPostComposer from './CommunityPostComposer'
import CommunityPostCard from './CommunityPostCard'
import { getCommunityPosts } from 'utils/teachingTeamApi'

export default function CommunityFeedSection({ teamId, teamSlug, colors, categories = [], canPost, canInteract, currentUser, translations = {}, onRequestJoin }) {
  const [posts, setPosts] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const sectionRef = useRef(null)
  const afterCursorRef = useRef(null)

  // Check if current user is owner or admin (passed via currentUser.role on the team)
  const isOwnerOrAdmin = !!(currentUser?.teamRole && ['owner', 'admin'].includes(currentUser.teamRole))

  const loadPosts = useCallback(async (categoryId, reset = false) => {
    if (reset) {
      setLoading(true)
      afterCursorRef.current = null
    } else {
      setLoadingMore(true)
    }
    try {
      const data = await getCommunityPosts(teamId, {
        categoryId: categoryId || undefined,
        after: reset ? undefined : afterCursorRef.current,
        limit: reset ? 2 : 10,
      })
      const newPosts = data.posts || []
      setPosts((prev) => reset ? newPosts : [...prev, ...newPosts])
      setHasMore(data.has_more || false)
      if (newPosts.length > 0) {
        afterCursorRef.current = newPosts[newPosts.length - 1].id
      }
    } catch (err) {
      console.error('Failed to load posts:', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [teamId])

  // Lazy load on scroll-into-view
  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loaded) {
          setLoaded(true)
          loadPosts(selectedCategoryId, true)
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loaded, loadPosts, selectedCategoryId])

  const handleCategorySelect = (categoryId) => {
    setSelectedCategoryId(categoryId)
    if (loaded) {
      loadPosts(categoryId, true)
    }
  }

  const handlePostCreated = (newPost) => {
    // Pinned posts go to top, others prepend after pinned ones
    setPosts((prev) => {
      const pinnedPosts = prev.filter((p) => p.pinned)
      const normalPosts = prev.filter((p) => !p.pinned)
      if (newPost.pinned) {
        return [newPost, ...pinnedPosts, ...normalPosts]
      }
      return [...pinnedPosts, newPost, ...normalPosts]
    })
  }

  const handlePostUpdated = (data) => {
    const updatedPost = data.post || data
    setPosts((prev) => {
      const updated = prev.map((p) => p.id === updatedPost.id ? updatedPost : p)
      return [...updated.filter((p) => p.pinned), ...updated.filter((p) => !p.pinned)]
    })
  }

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId))
  }

  const handleLoadMore = () => {
    loadPosts(selectedCategoryId, false)
  }

  return (
    <section ref={sectionRef} className="py-8 border-t border-gray-200">
      {/* Section header */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" style={{ color: colors?.primary || '#3366FF' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
          </svg>
          <h2 className="text-[22px] font-semibold" style={{ color: '#222222' }}>
            {translations.title || 'Community'}
          </h2>
        </div>
        {translations.subtitle && (
          <p className="text-sm mt-1" style={{ color: '#717171' }}>{translations.subtitle}</p>
        )}
      </div>

      {/* Category filter */}
      {categories.length > 0 && (
        <div className="mb-4">
          <CommunityCategoryFilter
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            onSelect={handleCategorySelect}
            colors={colors}
          />
        </div>
      )}

      {/* Composer */}
      {currentUser && (
        canPost ? (
          <div className="mb-4">
            <CommunityPostComposer
              teamId={teamId}
              categories={categories}
              currentUser={currentUser}
              colors={colors}
              isOwnerOrAdmin={isOwnerOrAdmin}
              onPostCreated={handlePostCreated}
              translations={translations}
            />
          </div>
        ) : (
          <div
            className="mb-4 bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3 cursor-pointer hover:border-gray-300 transition-colors"
            onClick={() => onRequestJoin && onRequestJoin()}
          >
            {currentUser.avatar_url ? (
              <img src={currentUser.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 text-sm font-semibold">
                {currentUser.name?.charAt(0) || '?'}
              </div>
            )}
            <span className="text-sm text-gray-400">{translations.write_something || 'Share your thoughts...'}</span>
          </div>
        )
      )}

      {/* Posts list. Before the section scrolls into view nothing has loaded,
          so show the placeholder rather than the "no posts yet" message. */}
      {loading || !loaded ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-3 bg-gray-200 rounded w-1/4" />
                  <div className="h-2.5 bg-gray-100 rounded w-1/6" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
          </svg>
          <p className="text-sm">{translations.empty_state || 'No posts yet. Be the first to share something!'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <CommunityPostCard
              key={post.id}
              post={post}
              teamId={teamId}
              teamSlug={teamSlug}
              currentUser={currentUser}
              colors={colors}
              isOwnerOrAdmin={isOwnerOrAdmin}
              canInteract={canInteract}
              onRequestJoin={onRequestJoin}
              onPostUpdated={handlePostUpdated}
              onPostDeleted={handlePostDeleted}
              translations={translations}
              categories={categories}
            />
          ))}
        </div>
      )}

      {/* View All link */}
      {teamSlug && posts.length > 0 && (
        <div className="mt-4 text-center">
          <a
            href={`/${teamSlug}/community`}
            className="text-sm font-medium hover:underline transition-colors"
            style={{ color: colors?.primary || '#3366FF' }}
          >
            {translations.view_all || 'View All'} &rarr;
          </a>
        </div>
      )}
    </section>
  )
}
